import { memo, useCallback } from 'react'
import { MessageList } from '../components/MessageList'
import SharedDiceRoll from '../components/SharedDiceRoll'
import type { SharedDiceRollingState } from '../GameControls/useDiceControls'
import type { Message } from '@/types/message'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface PublicChatProps {
  messages: Message[]
  characters: Character[]
  members: MemberType[]
  isMaster: boolean
  currentUserId: string
  sharedDiceRolling: SharedDiceRollingState
  /** A bandeja unica esta na tela: a cerimonia de dados nao aparece. */
  cerimoniaSuprimida?: boolean
  /** Envia ao chat a rolagem congelada na cerimonia (so quem rolou ve o botao). */
  onEnviarRolagem?: (rollId: string) => Promise<boolean>
  loadingHistory: boolean
  onUpdateMessage?: (messageId: string, content: string) => Promise<void>
  // Paginação
  onLoadMore?: () => Promise<void>
  loadingMore?: boolean
  hasMore?: boolean
}

export const PublicChat = memo(function PublicChat({
  messages,
  characters,
  members,
  isMaster,
  currentUserId,
  sharedDiceRolling,
  cerimoniaSuprimida = false,
  onEnviarRolagem,
  loadingHistory,
  onUpdateMessage,
  onLoadMore,
  loadingMore,
  hasMore
}: PublicChatProps) {
  // WebSocket atualiza mensagens automaticamente; este callback fica
  // estável pra não quebrar o memo do MessageList a cada re-render do pai.
  const noopMessagesChange = useCallback(() => {}, [])

  // Render UMA única MessageList — durante rolagem de dados, aplicamos
  // blur via classe e sobrepomos o overlay. Antes existiam duas
  // instâncias separadas no JSX e cada toggle desmontava/montava o
  // componente, perdendo scroll position e re-renderizando tudo.
  // Quem ROLOU ve a jogada na propria bandeja, onde montou os dados — a
  // cerimonia e para quem assiste. Sem isto, duas telas mostrariam a mesma
  // coisa para a mesma pessoa (decisao do Paulo, 2026-07-25).
  const isRolling = sharedDiceRolling.isActive
    && sharedDiceRolling.userId !== currentUserId
    // Com a bandeja unica aberta (fase A), a jogada acontece NELA para todos
    // — a cerimonia nao pula por cima. O sorteio de personagem nao e jogada
    // de dado e mantem a cerimonia sempre.
    && (!cerimoniaSuprimida || sharedDiceRolling.diceType === 'sorteio')

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
      <MessageList
        messages={messages}
        currentUserId={currentUserId}
        chatMode="group"
        className={
          isRolling
            ? 'flex-1 min-h-0 blur-sm opacity-30 transition-[filter,opacity] duration-200'
            : 'flex-1 min-h-0 transition-[filter,opacity] duration-200'
        }
        characters={characters}
        members={members}
        isMaster={isMaster}
        isLoading={loadingHistory}
        onUpdateMessage={onUpdateMessage}
        onMessagesChange={noopMessagesChange}
        onLoadMore={onLoadMore}
        loadingMore={loadingMore}
        hasMore={hasMore}
      />

      {isRolling && (
        <SharedDiceRoll
          isActive={sharedDiceRolling.isActive}
          player={sharedDiceRolling.player}
          diceType={sharedDiceRolling.diceType}
          startTime={sharedDiceRolling.startTime}
          userId={sharedDiceRolling.userId}
          characterName={sharedDiceRolling.characterName}
          result={sharedDiceRolling.result}
          rollId={sharedDiceRolling.rollId}
          diceSpec={sharedDiceRolling.diceSpec}
          kept={sharedDiceRolling.kept}
          rule={sharedDiceRolling.rule}
          expression={sharedDiceRolling.expression}
          modifiers={sharedDiceRolling.modifiers}
          attributeName={sharedDiceRolling.attributeName}
          attributeValue={sharedDiceRolling.attributeValue}
          bonus={sharedDiceRolling.bonus}
          total={sharedDiceRolling.total}
          difficulty={sharedDiceRolling.difficulty}
          outcome={sharedDiceRolling.outcome}
          souEuQuemRolou={sharedDiceRolling.userId === currentUserId}
          souMestre={isMaster}
          onEnviar={onEnviarRolagem}
          className="absolute inset-0 z-10"
        />
      )}
    </div>
  )
})